export type TransportProcentualMultiProductMode = 'per_product' | 'percentage'

export type TransportIntervalsCartMode =
  | 'per_product_or_percentage'
  | 'always_sum'
  | 'single_parcel'

export type TransportIntervalsQuantityMode = 'times_qty' | 'once' | 'weight_times_qty'

export interface TransportIntervalBand {
  minKg: number
  maxKg: number
  minLengthCm?: number | null
  maxLengthCm?: number | null
  minWidthCm?: number | null
  maxWidthCm?: number | null
  minHeightCm?: number | null
  maxHeightCm?: number | null
  priceTur: number
  priceRetur: number
}

export interface ShippingMethodsPublicConfig {
  store_pickup: boolean
  transport_procentual: boolean
  transport_procentual_mode: TransportProcentualMultiProductMode
  transport_procentual_percentage: number
  transport_intervals: boolean
  transport_intervals_cart_mode: TransportIntervalsCartMode
  transport_intervals_quantity_mode: TransportIntervalsQuantityMode
  transport_intervals_mode: TransportProcentualMultiProductMode
  transport_intervals_percentage: number
  transport_intervals_bands: TransportIntervalBand[]
}

function parseCartMode(value: unknown): TransportIntervalsCartMode {
  if (value === 'always_sum' || value === 'single_parcel') return value
  return 'per_product_or_percentage'
}

function parseQuantityMode(value: unknown): TransportIntervalsQuantityMode {
  if (value === 'once' || value === 'weight_times_qty') return value
  return 'times_qty'
}

function optionalDimension(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function parseIntervalBand(raw: unknown): TransportIntervalBand | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const minKg = Number(row.minKg)
  const maxKg = Number(row.maxKg)
  if (!Number.isFinite(minKg) || !Number.isFinite(maxKg)) return null
  return {
    minKg: Math.max(0, minKg),
    maxKg: Math.max(0, maxKg),
    minLengthCm: optionalDimension(row.minLengthCm),
    maxLengthCm: optionalDimension(row.maxLengthCm),
    minWidthCm: optionalDimension(row.minWidthCm),
    maxWidthCm: optionalDimension(row.maxWidthCm),
    minHeightCm: optionalDimension(row.minHeightCm),
    maxHeightCm: optionalDimension(row.maxHeightCm),
    priceTur: Math.max(0, Number(row.priceTur) || 0),
    priceRetur: Math.max(0, Number(row.priceRetur) || 0),
  }
}

const EMPTY_SHIPPING_METHODS: ShippingMethodsPublicConfig = {
  store_pickup: true,
  transport_procentual: false,
  transport_procentual_mode: 'per_product',
  transport_procentual_percentage: 0,
  transport_intervals: false,
  transport_intervals_cart_mode: 'per_product_or_percentage',
  transport_intervals_quantity_mode: 'times_qty',
  transport_intervals_mode: 'per_product',
  transport_intervals_percentage: 0,
  transport_intervals_bands: [],
}

export function parseShippingMethodsPublicConfig(raw?: string): ShippingMethodsPublicConfig {
  if (!raw) return { ...EMPTY_SHIPPING_METHODS }

  try {
    const parsed = JSON.parse(raw) as Partial<ShippingMethodsPublicConfig>
    const intervalsOn = parsed.transport_intervals === true
    return {
      store_pickup: parsed.store_pickup !== false,
      transport_procentual: !intervalsOn && parsed.transport_procentual === true,
      transport_procentual_mode: parsed.transport_procentual_mode === 'percentage'
        ? 'percentage'
        : 'per_product',
      transport_procentual_percentage: Math.max(0, Number(parsed.transport_procentual_percentage) || 0),
      transport_intervals: intervalsOn,
      transport_intervals_cart_mode: parseCartMode(parsed.transport_intervals_cart_mode),
      transport_intervals_quantity_mode: parseQuantityMode(parsed.transport_intervals_quantity_mode),
      transport_intervals_mode: parsed.transport_intervals_mode === 'percentage'
        ? 'percentage'
        : 'per_product',
      transport_intervals_percentage: Math.max(0, Number(parsed.transport_intervals_percentage) || 0),
      transport_intervals_bands: Array.isArray(parsed.transport_intervals_bands)
        ? parsed.transport_intervals_bands.map(parseIntervalBand).filter((row): row is TransportIntervalBand => row !== null)
        : [],
    }
  } catch {
    return { ...EMPTY_SHIPPING_METHODS }
  }
}
